/* eslint-disable max-lines */
/**
 * UnusedItemsReport
 *
 * "Dead presentations / slides" admin report (#14). Lists slides and saved
 * presentations with no event activity in the last N days. The stale window
 * default comes from the slide-sense route's `slide_sense_stale_window_days`
 * backend override; the user can override per-view via the input.
 *
 * Rows render with thumbnails so admins can recognize content before
 * acting on it. Slides open the standard preview modal (with admin edit
 * affordances); presentations show their slide-strip plus a delete button.
 */

import { useEffect, useState } from "react"

import { useQuery, useQueryClient } from "@tanstack/react-query"
import { formatDistanceToNow, type Locale } from "date-fns"
import {
  de as deLocale,
  enUS,
  fi as fiLocale,
  nb as noLocale,
  sv as svLocale,
} from "date-fns/locale"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { SlideSenseSlide } from "@/features/slide-sense/types"

import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Eye,
  Inbox,
  Loader2,
  Trash2,
} from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  adminDeleteCollection,
  fetchAdminSlide,
  fetchUsageUnused,
  type UsageUnusedCollection,
  type UsageUnusedSlide,
} from "@/features/slide-sense/admin-api"
import { cn } from "@/lib/utils"

import { CollectionDetailsDialog } from "./CollectionDetailsDialog"
import { SlidePreviewModal } from "../SlidePreviewModal"
import { SlideThumbnailImage } from "../SlideThumbnailImage"

const DATE_LOCALES: Record<string, Locale | undefined> = {
  sv: svLocale,
  en: enUS,
  no: noLocale,
  de: deLocale,
  fi: fiLocale,
}

function formatWhen(iso: string | null, lang: string): string {
  if (!iso) return "—"
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return "—"
  const locale = DATE_LOCALES[lang.split("-")[0] ?? "en"] ?? enUS
  return formatDistanceToNow(d, { addSuffix: true, locale })
}

export function UnusedItemsReport() {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const [days, setDays] = useState<number | null>(null)
  const [draftDays, setDraftDays] = useState<string>("")
  const [previewSlide, setPreviewSlide] = useState<SlideSenseSlide | null>(null)
  const [previewSlides, setPreviewSlides] = useState<SlideSenseSlide[]>([])
  const [openCollectionId, setOpenCollectionId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<UsageUnusedCollection | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [slidesExpanded, setSlidesExpanded] = useState(false)
  const [collectionsExpanded, setCollectionsExpanded] = useState(false)

  // The "applied" days value (null = use server default, an int = explicit
  // user override). Drives the query key.
  // The "draft" is what's currently in the input — applied on Refresh/Enter.
  // `autoSyncedTo` is a marker for what we last auto-set the draft to;
  // a mismatch between draftDays and String(autoSyncedTo) means the user
  // has typed something we shouldn't clobber.
  const [autoSyncedTo, setAutoSyncedTo] = useState<number | null>(null)

  const effectiveDays = days ?? undefined
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["slide-sense", "usage", "unused", effectiveDays ?? "default"],
    queryFn: () => fetchUsageUnused(effectiveDays),
    // Short stale window so an admin changing the route override picks up
    // quickly on next refresh — the report doesn't need minute-level
    // caching to be cheap.
    staleTime: 5_000,
  })

  // Keep the input synced with whatever default the server currently reports
  // (the route override). Distinguishes "user typed something" from "we
  // just auto-synced" by comparing the input against the last-seeded value
  // instead of just checking `days !== null`. The old check incorrectly
  // marked the input as "user-edited" the moment they clicked Refresh,
  // even though clicking Refresh on the seeded value is not an edit.
  const serverDefault = data?.default_stale_window_days ?? null
  useEffect(() => {
    if (serverDefault === null) return
    const userIsEditing = autoSyncedTo !== null && draftDays !== String(autoSyncedTo)
    if (userIsEditing) {
      // Track the new default but don't overwrite the in-progress edit.
      setAutoSyncedTo(serverDefault)
      return
    }
    if (draftDays !== String(serverDefault)) {
      setDraftDays(String(serverDefault))
    }
    setAutoSyncedTo(serverDefault)
  }, [serverDefault, draftDays, autoSyncedTo])

  const handleApply = async () => {
    const parsed = Number.parseInt(draftDays, 10)
    if (!data || !Number.isFinite(parsed)) {
      toast.error(t("slideSense.usage.unused.invalidDays"))
      return
    }
    if (parsed < data.min_days || parsed > data.max_days) {
      toast.error(
        t("slideSense.usage.unused.daysOutOfRange", {
          min: data.min_days,
          max: data.max_days,
        })
      )
      return
    }
    // If the input matches the server default, leave the override unset
    // (appliedDays = null). That way future override changes flow through
    // automatically without the local value pinning the query.
    const next = serverDefault !== null && parsed === serverDefault ? null : parsed
    if (next === days) {
      // No state transition would happen — force a manual refetch so
      // Refresh actually does something when the value is unchanged.
      await refetch()
      return
    }
    setDays(next)
  }

  const openSlideById = async (slideId: string) => {
    try {
      const slide = await fetchAdminSlide(slideId)
      setPreviewSlides([slide])
      setPreviewSlide(slide)
    } catch (err) {
      console.error("Failed to load slide:", err)
      toast.error(t("slideSense.usage.unused.slideLoadFailed"))
    }
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    setIsDeleting(true)
    try {
      await adminDeleteCollection(deleteTarget.collection_id)
      toast.success(
        t("slideSense.usage.unused.collectionDeleted", {
          name: deleteTarget.name || deleteTarget.collection_id,
        })
      )
      await queryClient.invalidateQueries({ queryKey: ["slide-sense", "usage", "unused"] })
      setDeleteTarget(null)
    } catch (err) {
      console.error("Failed to delete collection:", err)
      toast.error(t("slideSense.usage.unused.collectionDeleteFailed"))
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <section className="mt-12">
      <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">
            {t("slideSense.usage.unused.title")}
          </h2>
          <p className="text-muted-foreground mt-1 text-sm">
            {t("slideSense.usage.unused.description")}
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div className="flex flex-col gap-1">
            <label
              className="text-muted-foreground text-[11px] tracking-wider uppercase"
              htmlFor="dead-days-input"
            >
              {t("slideSense.usage.unused.daysLabel")}
            </label>
            <Input
              className="h-8 w-24 text-sm"
              id="dead-days-input"
              inputMode="numeric"
              max={data?.max_days}
              min={data?.min_days}
              type="number"
              value={draftDays}
              onChange={(e) => setDraftDays(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleApply()
              }}
            />
          </div>
          <Button
            disabled={isFetching || !data}
            size="sm"
            variant="outline"
            onClick={() => void handleApply()}
          >
            {isFetching ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
            {t("slideSense.usage.unused.apply")}
          </Button>
        </div>
      </header>

      {data && (
        <div className="border-warning-ink/40 bg-warning/30 mb-4 flex items-start gap-2 rounded-md border px-3 py-2 text-xs leading-relaxed">
          <AlertTriangle className="text-warning-ink h-3.5 w-3.5 shrink-0" />
          <p className="text-warning-ink">
            {t("slideSense.usage.unused.ttlCaveat", {
              ttl: data.event_ttl_days,
              window: data.window_days,
            })}
          </p>
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
        </div>
      )}

      {isError && (
        <div className="bg-destructive/10 text-destructive rounded-md p-4 text-sm">
          {t("slideSense.usage.unused.error")}
          <Button className="ml-3" size="sm" variant="outline" onClick={() => void refetch()}>
            {t("slideSense.usage.unused.retry")}
          </Button>
        </div>
      )}

      {data && (
        <div className="grid gap-6 lg:grid-cols-2">
          <ExpandableList
            count={data.unused_slides.length}
            emptyMessage={t("slideSense.usage.unused.slidesEmpty")}
            expanded={slidesExpanded}
            shownTotal={data.unused_slides_total}
            title={t("slideSense.usage.unused.slidesTitle")}
            onToggleExpanded={() => setSlidesExpanded((v) => !v)}
          >
            {data.unused_slides.map((s) => (
              <SlideRow
                key={s.slide_id}
                slide={s}
                when={formatWhen(s.updated_at, i18n.language)}
                onOpen={() => void openSlideById(s.slide_id)}
              />
            ))}
          </ExpandableList>

          <ExpandableList
            count={data.unused_collections.length}
            emptyMessage={t("slideSense.usage.unused.presentationsEmpty")}
            expanded={collectionsExpanded}
            shownTotal={data.unused_collections_total}
            title={t("slideSense.usage.unused.presentationsTitle")}
            onToggleExpanded={() => setCollectionsExpanded((v) => !v)}
          >
            {data.unused_collections.map((c) => (
              <CollectionRow
                key={c.collection_id}
                collection={c}
                when={formatWhen(c.updated_at, i18n.language)}
                onOpen={() => setOpenCollectionId(c.collection_id)}
                onRequestDelete={() => setDeleteTarget(c)}
              />
            ))}
          </ExpandableList>
        </div>
      )}

      <SlidePreviewModal
        canEdit
        open={previewSlide !== null}
        slide={previewSlide}
        slides={previewSlides}
        onNavigate={setPreviewSlide}
        onClose={() => {
          setPreviewSlide(null)
          setPreviewSlides([])
        }}
      />

      <CollectionDetailsDialog
        collectionId={openCollectionId}
        open={openCollectionId !== null}
        onClose={() => setOpenCollectionId(null)}
        onPreviewSlide={(slide, slides) => {
          setPreviewSlides(slides)
          setPreviewSlide(slide)
        }}
      />

      <AlertDialog open={deleteTarget !== null} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("slideSense.usage.unused.deleteConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("slideSense.usage.unused.deleteConfirmBody", {
                name: deleteTarget?.name || "",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t("slideSense.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              disabled={isDeleting}
              onClick={(e) => {
                e.preventDefault()
                void confirmDelete()
              }}
            >
              {isDeleting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              {t("slideSense.usage.unused.deleteConfirmAction")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}

// How many rows to show before the "Visa fler" expand button kicks in.
const COLLAPSED_LIMIT = 10

interface ExpandableListProps {
  title: string
  /** Total dead items the server reports — used for the header count. */
  shownTotal: number
  /** Number of rows currently passed as children. */
  count: number
  expanded: boolean
  onToggleExpanded: () => void
  emptyMessage: string
  children: React.ReactNode
}

function ExpandableList({
  title,
  shownTotal,
  count,
  expanded,
  onToggleExpanded,
  emptyMessage,
  children,
}: ExpandableListProps) {
  const { t } = useTranslation()
  const childArray = Array.isArray(children) ? children : [children]
  const visibleChildren = expanded ? childArray : childArray.slice(0, COLLAPSED_LIMIT)
  const overflow = count - COLLAPSED_LIMIT

  return (
    <div className="border-border/45 overflow-hidden rounded-lg border">
      <header className="flex items-center justify-between border-b px-4 py-2.5">
        <h3 className="text-sm font-semibold">{title}</h3>
        <span className="text-muted-foreground text-xs tabular-nums">
          {t("slideSense.usage.unused.count", { count: shownTotal })}
        </span>
      </header>
      {count === 0 ? (
        <div className="text-muted-foreground flex flex-col items-center justify-center gap-2 py-8 text-sm">
          <Inbox className="text-muted-foreground/40 h-6 w-6" />
          <p>{emptyMessage}</p>
        </div>
      ) : (
        <>
          <ul
            className={cn(
              "divide-border/45 divide-y",
              // Once expanded, cap the list height and scroll inside the
              // card so the rest of the page stays in view.
              expanded && "max-h-[36rem] overflow-y-auto"
            )}
          >
            {visibleChildren}
          </ul>
          {overflow > 0 && (
            <button
              className="border-border/45 text-muted-foreground hover:bg-accent/40 hover:text-foreground flex w-full items-center justify-center gap-1.5 border-t px-4 py-2 text-xs transition-colors"
              type="button"
              onClick={onToggleExpanded}
            >
              {expanded ? (
                <>
                  <ChevronUp className="h-3.5 w-3.5" />
                  {t("slideSense.usage.unused.showLess")}
                </>
              ) : (
                <>
                  <ChevronDown className="h-3.5 w-3.5" />
                  {t("slideSense.usage.unused.showMore", { count: overflow })}
                </>
              )}
            </button>
          )}
        </>
      )}
    </div>
  )
}

interface SlideRowProps {
  slide: UsageUnusedSlide
  when: string
  onOpen: () => void
}

function SlideRow({ slide, when, onOpen }: SlideRowProps) {
  const { t } = useTranslation()
  return (
    <li className="flex items-center gap-3 px-4 py-2.5">
      <button
        className="bg-muted/40 focus-visible:ring-ring relative h-14 w-24 shrink-0 overflow-hidden rounded-md border focus-visible:ring-2 focus-visible:outline-none"
        type="button"
        aria-label={t("slideSense.usage.unused.openItem", {
          name: slide.ai_title || slide.slide_id,
        })}
        onClick={onOpen}
      >
        <SlideThumbnailImage
          alt={slide.ai_title ?? ""}
          className="h-full w-full object-contain"
          slideId={slide.slide_id}
        />
      </button>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{slide.ai_title || slide.slide_id}</p>
        <p className="text-muted-foreground truncate text-xs">
          {slide.deck_name || slide.deck_id || "—"}
        </p>
        <p className="text-muted-foreground/80 text-[11px]">{when}</p>
      </div>
      <Button className="shrink-0 gap-1.5" size="sm" variant="outline" onClick={onOpen}>
        <Eye className="h-3.5 w-3.5" />
        {t("slideSense.usage.unused.view")}
      </Button>
    </li>
  )
}

interface CollectionRowProps {
  collection: UsageUnusedCollection
  when: string
  onOpen: () => void
  onRequestDelete: () => void
}

function CollectionRow({ collection, when, onOpen, onRequestDelete }: CollectionRowProps) {
  const { t } = useTranslation()
  const previewIds = collection.preview_slide_ids
  const extra = Math.max(0, collection.slide_count - previewIds.length)
  return (
    <li className="space-y-2 px-4 py-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">
            {collection.name || collection.collection_id}
          </p>
          <p className="text-muted-foreground truncate text-xs">
            {t("slideSense.usage.unused.collectionMeta", {
              count: collection.slide_count,
              owner: collection.owner_name || collection.owner_user_id || "—",
            })}
          </p>
          <p className="text-muted-foreground/80 text-[11px]">{when}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            className="gap-1.5"
            size="sm"
            variant="outline"
            aria-label={t("slideSense.usage.unused.openItem", {
              name: collection.name || collection.collection_id,
            })}
            onClick={onOpen}
          >
            <Eye className="h-3.5 w-3.5" />
            {t("slideSense.usage.unused.view")}
          </Button>
          <Button
            aria-label={t("slideSense.usage.unused.delete")}
            className="text-destructive hover:bg-destructive/10 hover:text-destructive h-8 w-8 p-0"
            size="sm"
            title={t("slideSense.usage.unused.delete")}
            variant="ghost"
            onClick={onRequestDelete}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {previewIds.length > 0 && (
        <div className="flex items-center gap-1.5">
          {previewIds.map((sid) => (
            <button
              key={sid}
              aria-label={t("slideSense.usage.unused.openItem", { name: sid })}
              className="bg-muted/40 focus-visible:ring-ring relative h-9 w-16 shrink-0 overflow-hidden rounded-sm border focus-visible:ring-2 focus-visible:outline-none"
              type="button"
              onClick={onOpen}
            >
              <SlideThumbnailImage alt="" className="h-full w-full object-contain" slideId={sid} />
            </button>
          ))}
          {extra > 0 && (
            <span className="text-muted-foreground text-[11px] tabular-nums">+{extra}</span>
          )}
        </div>
      )}
    </li>
  )
}
